import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import type * as LType from 'leaflet';

import { LeafletMapWrapperComponent } from '../leaflet-map-wrapper/leaflet-map-wrapper.component';

import { MapDrawController, MapDrawTool } from '../leaflet-map-wrapper/map-draw.controller';
import {
  boundaryFromGeometry,
  describeShape,
  DrawnShape,
  formatShapeValue,
  isShapeInside,
  parseCoordinateValue,
  POINT_TOLERANCE_M,
} from '../leaflet-map-wrapper/map-draw.model';
import * as turf from '@turf/turf';

/** What the dialog is opened with. Only `aoi` is really load-bearing. */
export interface MapLocationDialogData {
  /** Dialog title — what is being picked, e.g. "Vessel location". */
  title?: string;
  /** One sentence saying what the picked place will be used FOR. */
  subtitle?: string;
  /**
   * The boundary a pick must stay inside — WKT or GeoJSON. Everything outside
   * it is dimmed and refuses a mark. Omit to allow the whole world.
   */
  aoi?: any;
  /** A previous value (`lng,lat` or `lng,lat;…`), drawn when the dialog opens. */
  value?: string;
  /** Which tools to offer. Defaults to all three. */
  tools?: MapDrawTool[];
  /** Verb on the confirm button. Defaults to "Submit", as every other dialog. */
  confirmLabel?: string;
}

/** What the dialog closes with. Closing with nothing means it was abandoned. */
export interface MapLocationDialogResult {
  /** `lng,lat` for a place, or a closed `lng,lat;…` ring for an area. */
  value: string;
  kind: 'point' | 'area';
}

const OUTSIDE_AOI = 'Select your observation inside the boundary.';
const POI_POINT_ONLY = 'For this POI, please select observation using the Point tool only.';

/**
 * PICK A PLACE OR AN AREA ON A MAP, INSIDE A BOUNDARY.
 *
 * Written for the tip-and-cue "Vessel location" observation and deliberately
 * generic: it knows about a boundary, a shape and a value string, and nothing
 * about observations, tasks or tip engines. Everything specific to the caller
 * arrives as copy (`title`, `subtitle`) and leaves as one value.
 *
 * WHY IT EXISTS. The value is a place inside an area the user has already
 * drawn, and it used to be asked for as typed `lng,lat` pairs — so the
 * coordinate order was a silent trap (both orders are in range and both are
 * accepted), a pasted WKT string was stripped to bare digits by an input
 * sanitiser and then reported as an invalid coordinate, and a pair outside the
 * boundary was accepted, carried past the dialog's close, and answered by a
 * second modal that discarded the caller's entire selection. Marking it inside
 * a masked boundary removes all three as categories rather than improving their
 * messages.
 *
 * The typed field survives as a fallback, because coordinates do arrive in
 * emails and reports. It parses WKT as well as the stored `lng,lat;…` grammar
 * and stays in step with the map both ways.
 *
 * `MapDrawController` owns the Leaflet half; `LeafletMapWrapperComponent`
 * renders the boundary and frames it. Neither is modified by this.
 */
@Component({
  selector: 'app-map-location-dialog',
  templateUrl: './map-location-dialog.component.html',
  styleUrls: ['./map-location-dialog.component.scss'],
})
export class MapLocationDialogComponent implements OnInit, OnDestroy {
  /** The boundary as a shape: the dim mask and the containment test. */
  boundary: DrawnShape | null = null;
  /** What is marked right now, valid or not. */
  shape: DrawnShape | null = null;
  /** Why the marked shape cannot be used, or null. */
  error: string | null = null;
  /** The armed tool, so the tool row can show which. */
  tool: MapDrawTool | null = null;
  /**
   * The tools on offer, in the order they appear in the map's own toolbar —
   * area tools first, because an area is what this value usually is.
   *
   * The BUTTONS are not this component's: they are leaflet-draw's, on the map,
   * dressed by the stylesheet to match the tasking screen's toolbar. This list
   * only decides which of them exist. None is armed until one is clicked, and
   * a tool lasts one shape — see `MapDrawController`.
   */
  readonly offeredTools: MapDrawTool[];
  /** True while the map box is the fullscreen element. */
  maximized = false;
  /** Raw contents of the typed fallback. */
  typed = '';
  /** The typed fallback starts collapsed — the map is the path being offered. */
  typedOpen = false;
  /**
   * A POINT boundary has no area to mark inside, so the only pick it can accept
   * is that same point. Stated and pre-filled rather than offering tools that
   * could only produce a refusal. (A POI task's target is such a boundary.)
   */
  fixedToBoundary = false;

  /** How far a marked place reaches, for the copy that says so. */
  readonly toleranceM = POINT_TOLERANCE_M;

  /** The boundary outline under the mask — muted, never in the way of a click. */
  readonly boundaryStyle = {
    color: '#f5a524',
    weight: 2,
    opacity: 1,
    fillOpacity: 0,
    dashArray: '6 4',
    interactive: false,
  };

  private draw: MapDrawController | null = null;
  // Stable geoJSON supplied to the wrapper so it renders the buffered POI
  // (avoid a getter that allocates a new object on every change detection).
  wrapperGeoJson: any = null;
  // Original POI kept as a fixed reference marker when boundary started as point.
  private poiReferencePoint: [number, number] | null = null;
  private isBufferedPoiBoundary = false;

  /** The box that goes fullscreen — the map and its controls, nothing else. */
  @ViewChild('mapBox') private readonly mapBox?: ElementRef<HTMLElement>;
  /** Asked to re-measure after a fullscreen change, which resizes its container. */
  @ViewChild(LeafletMapWrapperComponent) private readonly wrapper?: LeafletMapWrapperComponent;

  constructor(
    private readonly dialogRef: MatDialogRef<MapLocationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: MapLocationDialogData
  ) {
    this.offeredTools = this.data?.tools ?? ['rectangle', 'polygon', 'point'];
  }

  ngOnInit(): void {
    this.boundary = boundaryFromGeometry(this.data?.aoi);

    if (this.boundary?.kind === 'point' && this.boundary.point) {
      const [lng, lat]: any = this.boundary.point;
      this.poiReferencePoint = [lng, lat];
      this.isBufferedPoiBoundary = true;

      // Do not pre-create observation from POI.
      this.shape = null;
      this.typed = '';

      // Turn the POI into a small AOI so containment/mask/tooling match AOI behavior.
      const radiusMeters = POINT_TOLERANCE_M;
      const buffered = turf.buffer(turf.point([lng, lat]), radiusMeters, { units: 'meters' });
      const coords = buffered?.geometry?.coordinates?.[0] ?? [];
      if (Array.isArray(coords) && coords.length >= 4) {
        this.boundary = { kind: 'area', ring: coords.map((c: any) => [c[0], c[1]]) };
        this.fixedToBoundary = false;
      }
    }

    this.wrapperGeoJson = this.buildWrapperGeoJson();

    // A value carried back in — re-opening the same question.
    const carried = parseCoordinateValue(this.data?.value ?? '');
    if (carried.shape) {
      if (this.isReferencePoi(carried.shape)) {
        this.shape = null;
        this.typed = '';
        this.error = null;
      } else if (this.isBufferedPoiBoundary && carried.shape.kind !== 'point') {
        this.shape = null;
        this.typed = '';
        this.error = POI_POINT_ONLY;
      } else {
        this.shape = carried.shape;
        this.typed = formatShapeValue(carried.shape);
        this.error = isShapeInside(carried.shape, this.boundary) ? null : OUTSIDE_AOI;
      }
    }
    this.wrapperGeoJson = this.buildWrapperGeoJson();
  }

  ngOnDestroy(): void {
    this.draw?.destroy();
    this.draw = null;
  }

  // ── Copy, with defaults so a caller can pass almost nothing ───────────────

  get title(): string {
    return this.data?.title || 'Pick a location';
  }

  get confirmLabel(): string {
    return this.data?.confirmLabel || 'Submit';
  }

  /**
   * What to do next, over the map, in one fixed place at the bottom.
   *
   * This is also where leaflet-draw's own instructions live now: its tooltip
   * chases the cursor, covers the very ground being drawn on and reads as a
   * hover artefact rather than guidance, so the stylesheet hides it and its
   * copy is said here instead — including the part a polygon actually needs,
   * which is how to close the ring.
   */
  get hint(): string {
    // if (this.tool === 'point') return 'Click inside the highlighted target to mark a place';
    // if (this.tool === 'polygon') {
    //   return 'Click to add corners inside the target, then click the first corner to close';
    // }
    // if (this.tool === 'rectangle') return 'Drag inside the highlighted target to draw a rectangle';
    return 'Pick a tool from the map, and select an observation inside the boundary.';
  }

  /** Nothing marked, something wrong, or good to go — the read-back's tone. */
  get state(): 'idle' | 'bad' | 'ok' {
    if (this.error) return 'bad';
    return this.shape ? 'ok' : 'idle';
  }

  /** "A place — 25.161° N, 55.342° E" / "An area - 4 corners: 132 sq km". */
  get summary(): { title: string; detail: string } | null {
    return this.shape ? describeShape(this.shape) : null;
  }

  get canSubmit(): boolean {
    return !!this.shape && !this.error;
  }

  /** Why the confirm button is off, said beside it rather than left to guess. */
  get blockedReason(): string | null {
    if (this.canSubmit) return null;
    return this.error ? 'There is an error highlighted. Rectify it to continue.' : 'Select an observation on the map to submit.';
  }

  // ── The map ───────────────────────────────────────────────────────────────

  /**
   * The wrapper has a map. It renders the boundary and frames it; everything
   * drawn on top is the controller's, added beside the wrapper's own layer
   * rather than through it — the wrapper only ever removes what it rendered.
   */
  onMapReady(map: LType.Map): void {
    this.draw?.destroy();
    const tools: MapDrawTool[] = this.fixedToBoundary
      ? []
      : this.isBufferedPoiBoundary
        ? ['point']
        : this.offeredTools;
    this.draw = new MapDrawController(map, {
      // A point boundary has nothing to draw inside, so it gets no toolbar —
      // offering tools that could only ever be refused is worse than offering
      // none. The read-back says so in words.
      tools,
      boundary: this.boundary,
      showPointTolerance: false,
      onChange: (shape) => this.onShapeChange(shape),
      onReject: (reason) => (this.error = reason || OUTSIDE_AOI),
      onToolChange: (tool) => (this.tool = tool),
    });
    if (this.shape && !this.isReferencePoi(this.shape)) {
      this.draw.setShape(this.shape, { fit: true });
    }
  }

  /**
   * Full screen for the map box, the same gesture and the same artwork the
   * collections map uses — `requestFullscreen` on the element, then a
   * re-measure, because Leaflet sizes itself from a container that just changed.
   */
  toggleMaximize(): void {
    const el: any = this.mapBox?.nativeElement;
    if (!el) return;

    const done = () => setTimeout(() => this.wrapper?.invalidateSize(), 0);
    if (!document.fullscreenElement) {
      const request =
        el.requestFullscreen ||
        el.mozRequestFullScreen ||
        el.webkitRequestFullscreen ||
        el.msRequestFullscreen;
      if (request) Promise.resolve(request.call(el)).then(done).catch(() => undefined);
      return;
    }
    if (document.exitFullscreen) {
      document.exitFullscreen().then(done).catch(() => undefined);
    }
  }

  /**
   * Fullscreen can also be left with Escape, which never goes through the
   * button — so the icon follows the DOCUMENT rather than the click.
   */
  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    this.maximized = !!document.fullscreenElement;
    setTimeout(() => this.wrapper?.invalidateSize(), 0);
  }

  /** Drawn or edited on the map — the field follows the map. */
  private onShapeChange(shape: DrawnShape | null): void {
    this.shape = shape;
    this.error = null;
    this.typed = formatShapeValue(shape);
  }

  /**
   * Typed or pasted — the map follows the field.
   *
   * A value that parses but falls outside the boundary is still DRAWN, with the
   * error beside it: showing where the coordinates actually landed is what
   * makes "outside the target area" a fact the user can act on rather than an
   * assertion they have to take on trust.
   */
  onTypedInput(text: string): void {
    this.typed = text;
    const { shape, error } = parseCoordinateValue(text);
    this.shape = shape;

    if (!shape) {
      this.error = error;
      this.draw?.setShape(null);
      return;
    }

    if (this.isBufferedPoiBoundary && shape.kind !== 'point') {
      this.shape = null;
      this.error = POI_POINT_ONLY;
      this.draw?.setShape(null);
      return;
    }

    this.draw?.setShape(shape, { fit: true });
    this.error = isShapeInside(shape, this.boundary) ? null : OUTSIDE_AOI;
  }

  // ── Closing ───────────────────────────────────────────────────────────────

  submit(): void {
    if (!this.shape) {
      this.error = 'Mark the place on the map first.';
      return;
    }
    if (this.isBufferedPoiBoundary && this.shape.kind !== 'point') {
      this.error = POI_POINT_ONLY;
      return;
    }
    if (!isShapeInside(this.shape, this.boundary)) {
      this.error = OUTSIDE_AOI;
      return;
    }
    const result: MapLocationDialogResult = {
      value: formatShapeValue(this.shape),
      kind: this.shape.kind,
    };
    this.dialogRef.close(result);
  }

  /** Closing with nothing is how the caller knows it was abandoned. */
  cancel(): void {
    this.dialogRef.close();
  }

  private buildWrapperGeoJson(): any {
    if (this.isBufferedPoiBoundary && this.boundary?.kind === 'area' && this.boundary.ring && this.poiReferencePoint) {
      return {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: { role: 'boundary' },
            geometry: { type: 'Polygon', coordinates: [this.boundary.ring] },
          },
          {
            type: 'Feature',
            properties: { aoiTarget: true, role: 'poi-reference' },
            geometry: { type: 'Point', coordinates: this.poiReferencePoint },
          },
        ],
      };
    }

    if (this.boundary?.kind === 'area' && this.boundary.ring) {
      return {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [this.boundary.ring] },
      };
    }

    return this.data?.aoi ?? null;
  }

  private isReferencePoi(shape: DrawnShape | null): boolean {
    if (!shape || shape.kind !== 'point' || !shape.point || !this.poiReferencePoint) return false;
    const [lngA, latA] = shape.point;
    const [lngB, latB] = this.poiReferencePoint;
    return Math.abs(lngA - lngB) < 1e-9 && Math.abs(latA - latB) < 1e-9;
  }
  
}
